import { computed, DestroyRef, inject, Service, signal } from '@angular/core';

import { ActionState } from '@shared/state/async-action';
import { PlatformService } from '@core/platform/platform.service';
import { OrganizationsRepository } from '../domain/interfaces/organizations.interface';
import {
  ORG_CONSENT_NOTE_MAX,
  OrgChild,
  OrgChildDigest,
  OrgConsentRequest,
  digestSections,
  hasGuardianSurface,
} from '../domain/models/org-guardian.model';
import { OrgGuardianRequest } from '../domain/models/org-counseling.model';
import { OrgError } from '../domain/org.errors';
import { OrgErrorMapper } from './org-error.mapper';
import { destroyNotifier, firstValueFromScoped, runOrgAction } from './org-action';

/**
 * OrgGuardianFacade — the guardian surfaces (§8).
 *
 * ⚠️ THIS FACADE DELIBERATELY NEVER READS `/org/context` (trap 20).
 *
 * Role is resolved PER ENDPOINT. A parent who is a guardian at their child's
 * school and nothing else gets `member: null` from `/org/context` AND a populated
 * `/org/children` — that is correct, not a bug. Gating this surface on the
 * context call would hide a real guardian's only reason to open the app.
 *
 * Authorisation is the GRANTED LINK, never the `guardian` role: being a guardian
 * somewhere grants nothing; being *this* child's linked guardian grants a
 * tier-filtered view of *this* child. A revoked or pending link is not a link,
 * and the server answers 404 for it — deliberately indistinguishable from "no
 * such child", which is a privacy property, not an oversight.
 */
@Service({ autoProvided: false })
export class OrgGuardianFacade {
  private readonly repo = inject(OrganizationsRepository);
  private readonly errors = inject(OrgErrorMapper);
  private readonly platform = inject(PlatformService);
  private readonly destroyed = destroyNotifier(inject(DestroyRef));

  private readonly _children = signal<readonly OrgChild[]>([]);
  private readonly _consents = signal<readonly OrgConsentRequest[]>([]);
  private readonly _requests = signal<readonly OrgGuardianRequest[]>([]);
  private readonly _digest = signal<OrgChildDigest | null>(null);
  private readonly _selectedChildId = signal<number | null>(null);

  private readonly _childrenError = signal<OrgError | null>(null);
  private readonly _consentsError = signal<OrgError | null>(null);
  private readonly _requestsError = signal<OrgError | null>(null);
  private readonly _digestError = signal<OrgError | null>(null);
  private readonly _decisionError = signal<OrgError | null>(null);
  private readonly _loadedAt = signal(0);
  /** The referral / session a decision is running for, so only that card shows busy. */
  private readonly _decidingId = signal<number | null>(null);

  readonly childrenLoad = new ActionState('reference_portal_org_children_failed', 'GET /org/children');
  readonly consentsLoad = new ActionState('reference_portal_org_consents_failed', 'GET /org/consents');
  readonly requestsLoad = new ActionState('reference_portal_org_requests_failed', 'GET /counseling/guardian-requests');
  readonly digestLoad = new ActionState('reference_portal_org_digest_failed', 'GET /org/children/{id}/digest');
  readonly decide = new ActionState('reference_portal_org_decision_failed', 'POST consent / guardian-request');

  readonly children = this._children.asReadonly();
  readonly consents = this._consents.asReadonly();
  readonly requests = this._requests.asReadonly();
  readonly digest = this._digest.asReadonly();
  readonly selectedChildId = this._selectedChildId.asReadonly();
  readonly childrenError = this._childrenError.asReadonly();
  readonly consentsError = this._consentsError.asReadonly();
  readonly requestsError = this._requestsError.asReadonly();
  readonly digestError = this._digestError.asReadonly();
  readonly decisionError = this._decisionError.asReadonly();
  readonly decidingId = this._decidingId.asReadonly();

  readonly isFirstLoad = computed(() => this._loadedAt() === 0 && !this.childrenLoad.failed());

  /**
   * Is there a guardian surface to show at all? Three empty 200s is the honest
   * "you are not a guardian here" answer (§8.1/§8.3) — an empty state, never a
   * permission error.
   */
  readonly hasSurface = computed(() => hasGuardianSurface(this._children(), this._consents(), this._requests()));

  readonly selectedChild = computed<OrgChild | null>(
    () => this._children().find((c) => c.memberId === this._selectedChildId()) ?? null,
  );

  /**
   * The ONLY correct way to walk a digest (trap 15): iterate `available_sections`
   * and look each one up, because `sections` is a SPARSE map — a permitted
   * section with nothing to show is simply absent.
   */
  readonly sections = computed(() => {
    const digest = this._digest();
    return digest ? digestSections(digest) : [];
  });

  readonly consentNoteMaxLength = ORG_CONSENT_NOTE_MAX;

  // ── reads ───────────────────────────────────────────────────────────────
  /**
   * Load all three guardian lists. Run in parallel and independently: a guardian
   * with children but no pending consents is the common case, and one empty list
   * must never stop the others from rendering.
   */
  async ensureLoaded(): Promise<void> {
    if (!this.platform.isBrowser || this._loadedAt() > 0) return;
    await this.reload();
  }

  async reload(): Promise<void> {
    await Promise.all([this.loadChildren(), this.loadConsents(), this.loadRequests()]);
    this._loadedAt.set(Date.now());
  }

  async loadChildren(): Promise<void> {
    const children = await runOrgAction(this.childrenLoad, this._childrenError, this.errors, () =>
      firstValueFromScoped(this.repo.getChildren(), this.destroyed),
    );
    if (children) this._children.set(children);
  }

  async loadConsents(): Promise<void> {
    const consents = await runOrgAction(this.consentsLoad, this._consentsError, this.errors, () =>
      firstValueFromScoped(this.repo.getConsents(), this.destroyed),
    );
    if (consents) this._consents.set(consents);
  }

  async loadRequests(): Promise<void> {
    const requests = await runOrgAction(this.requestsLoad, this._requestsError, this.errors, () =>
      firstValueFromScoped(this.repo.getGuardianRequests(), this.destroyed),
    );
    if (requests) this._requests.set(requests);
  }

  /**
   * Open a child's digest. `memberId` is the ORG MEMBER id from `/org/children`,
   * NOT a user id (trap 14).
   *
   * A 404 here covers both "no such child" and "your link was withdrawn" — the
   * server does not distinguish them, and neither does the copy.
   */
  async openDigest(memberId: number): Promise<void> {
    this._selectedChildId.set(memberId);
    this._digest.set(null);
    const digest = await runOrgAction(this.digestLoad, this._digestError, this.errors, () =>
      firstValueFromScoped(this.repo.getChildDigest(memberId), this.destroyed),
    );
    if (digest) this._digest.set(digest);
  }

  closeDigest(): void {
    this._selectedChildId.set(null);
    this._digest.set(null);
    this._digestError.set(null);
    this.digestLoad.reset();
  }

  // ── writes ──────────────────────────────────────────────────────────────
  /**
   * Decide a consent request. The scope and note were shown on the decision
   * screen — consent with no stated scope is not informed consent (§8.3) — so by
   * the time this runs the guardian has seen exactly what they are granting.
   */
  async decideConsent(referralId: number, granted: boolean, note?: string | null): Promise<boolean> {
    this._decidingId.set(referralId);
    try {
      const decision = await runOrgAction(this.decide, this._decisionError, this.errors, () =>
        firstValueFromScoped(this.repo.decideConsent(referralId, granted, note), this.destroyed),
      );
      if (!decision) return false;
      // The row is resolved — drop it locally so the list settles immediately,
      // then re-read so anything else that changed server-side comes with it.
      this._consents.set(this._consents().filter((c) => c.referralId !== decision.referralId));
      await this.loadConsents();
      return true;
    } finally {
      this._decidingId.set(null);
    }
  }

  /**
   * Approve or decline a counseling booking on the child's behalf (§8.5). The
   * response carries the refreshed list AND a ready-to-render sentence
   * («تم تأكيد الموعد.» / «تم رفض الطلب.») — shown verbatim.
   */
  async decideRequest(sessionId: number, approve: boolean): Promise<boolean> {
    this._decidingId.set(sessionId);
    try {
      const result = await runOrgAction(this.decide, this._decisionError, this.errors, () =>
        firstValueFromScoped(this.repo.decideGuardianRequest(sessionId, approve), this.destroyed),
      );
      if (!result) return false;
      this._requests.set(result.requests);
      if (result.message) this.errors.success(result.message);
      return true;
    } finally {
      this._decidingId.set(null);
    }
  }

  clearDecisionError(): void {
    this._decisionError.set(null);
    this.decide.reset();
  }
}
